import Add from '@mui/icons-material/Add';
import { paperClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import {
  ModelError_Item,
  ModelError_Response,
  OrderUnitOfMeasure,
} from '@treadinc/horizon-api-spec';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import { uniqBy } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { ActiveItemsVirtualLazyLoadList } from '~components/Lists/ActiveItemsVirtualLazyLoadList';
import {
  LoadingSpinner,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Account, AccountBasic } from '~hooks/useAccount/models';
import { MaterialRate } from '~hooks/useMaterialRates/models';
import useMaterialRates from '~hooks/useMaterialRates/useMaterialRates';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import MaterialRateForm, { MaterialRateFormHandler } from './components/MaterialRateForm';
import { materialRatePriceToPayload } from './components/materialRateSchema';
import MaterialRatesDataGrid, {
  MaterialRatesDataGridHandler,
} from './components/MaterialRatesDataGrid';
import MaterialRatesFilters, {
  MaterialRatesFiltersProps,
} from './components/MaterialRatesFilters';

interface CustomersListState {
  customers: Account[];
  isLoading: boolean;
  cursor?: string;
  nextPageCursor?: string;
}

enum MaterialRateDialogSource {
  ADD_RATE_CTA = 'add_rate_cta',
  ADD_SCHEDULE_CTA = 'add_schedule_cta',
}

interface MaterialRateDialogState {
  customer?: AccountBasic;
  errors?: ModelError_Item[];
  isOpen: boolean;
  isSaving: boolean;
  materialRate?: MaterialRate;
  source: MaterialRateDialogSource;
}

const MaterialRates = observer(function MaterialRates() {
  const theme = useTheme();
  const { toasterStore, userStore } = useStores();
  const {
    createMaterialRate,
    deleteMaterialRate,
    getCustomersWithMaterialRates,
    updateMaterialRate,
  } = useMaterialRates();

  const [customersListState, setCustomersListState] = useState<CustomersListState>({
    customers: [],
    isLoading: true,
  });

  const [materialRateDialogState, setMaterialRateDialogState] =
    useState<MaterialRateDialogState>({
      isOpen: false,
      isSaving: false,
      source: MaterialRateDialogSource.ADD_SCHEDULE_CTA,
    });

  const [appliedFilters, setAppliedFilters] = useState<
    MaterialRatesFiltersProps['appliedFilters']
  >({});

  const materialRatesDataGridRef = useRef<MaterialRatesDataGridHandler>(null);
  const materialRateFormRef = useRef<MaterialRateFormHandler>(null);

  const companyId = getEffectiveUserCompanyId(userStore);
  const previousCompanyIdRef = useRef(companyId);
  const customersRequestGenerationRef = useRef(0);
  const isCustomerSelected = Boolean(materialRateDialogState.customer?.id);
  const isEditingMaterialRate = Boolean(materialRateDialogState.materialRate?.id);

  const dialogTitle = useMemo(() => {
    if (materialRateDialogState.source === MaterialRateDialogSource.ADD_RATE_CTA) {
      if (isEditingMaterialRate) {
        return t('administration.material_rates.edit_rate_form_title');
      }

      return t('administration.material_rates.add_rate_form_title');
    }

    return t('administration.material_rates.add_customer_schedule_form_title');
  }, [materialRateDialogState.source, isEditingMaterialRate]);

  const fetchCustomersWithMaterialRates = useCallback(async () => {
    // Customers, cursors and filters only mean anything to the company they were
    // fetched for, and the company selector can change companies while this page
    // stays mounted. Carrying any of them across a switch would query the new
    // company with ids it does not own.
    const companyChanged = previousCompanyIdRef.current !== companyId;
    previousCompanyIdRef.current = companyId;

    const generation = customersRequestGenerationRef.current + 1;
    customersRequestGenerationRef.current = generation;

    if (companyChanged) {
      setCustomersListState((state) => ({ ...state, customers: [], isLoading: true }));
      setAppliedFilters({});
      setMaterialRateDialogState((state) => ({ ...state, customer: undefined }));
    }

    try {
      const response = await getCustomersWithMaterialRates({
        pagination: {
          limit: MAX_API_PAGE_SIZE,
          page: 1,
          after: companyChanged ? undefined : customersListState.cursor,
        },
      });

      // A slower response for a company the user has already switched away from
      // must not repopulate the list.
      if (generation !== customersRequestGenerationRef.current) {
        return;
      }

      setCustomersListState((state) => ({
        ...state,
        isLoading: false,
        customers: companyChanged
          ? response.data
          : uniqBy(state.customers.concat(response.data), (customer) => customer.id),
        nextPageCursor: response.pagination.after,
      }));
    } catch (error) {
      if (generation !== customersRequestGenerationRef.current) {
        return;
      }

      connection.handleRequestError(
        error,
        t('error_messages.material_rates.failed_to_fetch_customers_with_material_rates'),
      );

      setCustomersListState((state) => ({ ...state, isLoading: false, customers: [] }));
    }
  }, [customersListState.cursor, companyId]);

  const openDialog = useCallback(
    (source: MaterialRateDialogSource, materialRate?: MaterialRate) => {
      setMaterialRateDialogState((state) => ({
        ...state,
        isOpen: true,
        isSaving: false,
        materialRate,
        source,
      }));
    },
    [],
  );

  const closeDialog = useCallback(() => {
    setMaterialRateDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleEditMaterialRate = useCallback(
    (materialRate: MaterialRate) => {
      openDialog(MaterialRateDialogSource.ADD_RATE_CTA, materialRate);
    },
    [openDialog],
  );

  const handleDeleteMaterialRate = useCallback(
    async (materialRate: MaterialRate) => {
      try {
        await deleteMaterialRate(materialRate.id);

        toasterStore.push(
          alert(
            t('administration.material_rates.material_rate_deleted_message'),
            AlertTypes.success,
          ),
        );

        materialRatesDataGridRef.current?.refetch();
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.material_rates.failed_to_delete_material_rate'),
        );
      }
    },
    [deleteMaterialRate],
  );

  const handleMaterialRateFormSubmit = useCallback(async () => {
    const formData = await materialRateFormRef.current?.submit();

    if (!formData) {
      return;
    }

    try {
      setMaterialRateDialogState((state) => ({ ...state, isSaving: true }));

      if (isEditingMaterialRate) {
        await updateMaterialRate(String(materialRateDialogState.materialRate?.id), {
          material_id: formData.material.id,
          project_id: formData.project?.id || undefined,
          site_id: formData.site?.id || undefined,
          customer_account_id: formData.customer?.id || undefined,
          buy_price: materialRatePriceToPayload(formData.buyPrice),
          sell_price: materialRatePriceToPayload(formData.sellPrice),
          unit_of_measure: formData.unitOfMeasure.id as OrderUnitOfMeasure,
        });
      } else {
        await createMaterialRate({
          material_id: formData.material.id,
          project_id: formData.project?.id || undefined,
          site_id: formData.site?.id || undefined,
          customer_account_id: formData.customer?.id || undefined,
          buy_price: materialRatePriceToPayload(formData.buyPrice),
          sell_price: materialRatePriceToPayload(formData.sellPrice),
          unit_of_measure: formData.unitOfMeasure.id as OrderUnitOfMeasure,
        });
      }

      toasterStore.push(
        alert(
          isEditingMaterialRate
            ? t('administration.material_rates.material_rate_updated_message')
            : t('administration.material_rates.material_rate_created_message'),
          AlertTypes.success,
        ),
      );

      setMaterialRateDialogState((state) => ({
        ...state,
        isOpen: false,
        isSaving: false,
      }));

      const materialRateIsForCurrentlySelectedCustomer =
        materialRateDialogState.customer?.id === (formData.customer?.id || undefined);
      const materialRateHasCustomer = Boolean(formData.customer?.id);
      const defaultMaterialRateWasAssignedToCustomer =
        !materialRateDialogState.customer?.id && formData.customer?.id;

      if (
        materialRateIsForCurrentlySelectedCustomer ||
        defaultMaterialRateWasAssignedToCustomer
      ) {
        materialRatesDataGridRef.current?.refetch();
      } else if (materialRateHasCustomer) {
        const isCustomerInList = customersListState.customers.some((customer) => {
          return customer.id === formData.customer?.id;
        });

        if (!isCustomerInList) {
          fetchCustomersWithMaterialRates();
        }
      }
    } catch (error) {
      const e = error as AxiosError<ModelError_Response>;
      let badRequestErrors: ModelError_Item[] | undefined;

      if (e.response?.status === 422) {
        badRequestErrors = e.response?.data.error.errors;
      }

      setMaterialRateDialogState((state) => ({
        ...state,
        isSaving: false,
        errors: badRequestErrors,
      }));

      connection.handleRequestError(
        error,
        isEditingMaterialRate
          ? t('error_messages.material_rates.failed_to_update_material_rate')
          : t('error_messages.material_rates.failed_to_create_material_rate'),
        [422],
      );
    }
  }, [
    companyId,
    customersListState.customers,
    fetchCustomersWithMaterialRates,
    isEditingMaterialRate,
    materialRateDialogState.customer?.id,
    materialRateDialogState.materialRate?.id,
    updateMaterialRate,
  ]);

  const handleAppliedFiltersChange = useCallback<
    MaterialRatesFiltersProps['onAppliedFiltersChange']
  >((appliedFilters) => {
    setAppliedFilters(appliedFilters);
  }, []);

  useEffect(() => {
    fetchCustomersWithMaterialRates();
  }, [fetchCustomersWithMaterialRates]);

  return (
    <>
      <SettingsPageHeader
        title={t('administration.material_rates.title')}
        description={t('settings.page_descriptions.material_rates')}
      />

      <Box
        sx={{
          border: `solid 1px ${theme.palette.divider}`,
          display: 'flex',
          // 178px = app bar + SettingsPageHeader (title + subtitle + margin) +
          // this panel's top margin, so the panel bottom-aligns to the viewport.
          height: 'calc(100vh - 178px - var(--tread-agent-dock-height, 0px))',
          mb: -4,
          mt: 2,
        }}
      >
        <Box
          sx={{
            borderRight: `solid 1px ${theme.palette.divider}`,
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            height: '100%',
            maxWidth: '240px',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', p: 1.5, flex: 0 }}>
            <SmallButton
              color="brandV2Yellow"
              onClick={() => openDialog(MaterialRateDialogSource.ADD_SCHEDULE_CTA)}
              startIcon={<Add />}
            >
              {t('administration.material_rates.add_customer_schedule_cta')}
            </SmallButton>
          </Box>

          <Box
            sx={{
              borderTop: `solid 1px ${theme.palette.divider}`,
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              overflow: 'auto',
            }}
          >
            {customersListState.isLoading ? (
              <LoadingSpinner isVisible sx={{ mt: 2 }} />
            ) : (
              <>
                <ListItemButton
                  onClick={() => {
                    setAppliedFilters({});
                    setMaterialRateDialogState((state) => ({
                      ...state,
                      customer: undefined,
                    }));
                  }}
                  selected={!materialRateDialogState.customer?.id}
                  sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    borderRight: !materialRateDialogState.customer?.id
                      ? `2px solid ${theme.palette.primary.main}`
                      : '',
                    flex: 0,
                    minHeight: 48,
                  }}
                >
                  <ListItemText
                    primary={t('administration.rates.default_rate')}
                    sx={{ fontWeight: 600 }}
                  />
                </ListItemButton>

                <ActiveItemsVirtualLazyLoadList
                  activeItemId={materialRateDialogState.customer?.id ?? ''}
                  hasNextPage={Boolean(customersListState.nextPageCursor)}
                  itemHeight={48}
                  items={customersListState.customers}
                  loadNextPage={() => {
                    setCustomersListState((state) => ({
                      ...state,
                      cursor: state.nextPageCursor,
                    }));
                  }}
                  onClickCallBack={(customer: Account) => {
                    setAppliedFilters({});
                    setMaterialRateDialogState((state) => ({ ...state, customer }));
                  }}
                  titleProperty="name"
                />
              </>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: 1.5 }}>
          {customersListState.isLoading ? (
            <LoadingSpinner isVisible sx={{ mt: 2 }} />
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', flex: 0 }}>
                <MaterialRatesFilters
                  appliedFilters={appliedFilters}
                  companyId={companyId}
                  customerAccountId={materialRateDialogState.customer?.id}
                  onAppliedFiltersChange={handleAppliedFiltersChange}
                />

                {isCustomerSelected && (
                  <SmallButton
                    color="brandV2Yellow"
                    onClick={() => openDialog(MaterialRateDialogSource.ADD_RATE_CTA)}
                    startIcon={<Add />}
                  >
                    {t('administration.material_rates.add_material_rate_cta')}
                  </SmallButton>
                )}
              </Box>

              <Box sx={{ flex: 1, mt: 1.5 }}>
                <MaterialRatesDataGrid
                  key={`${companyId}-${materialRateDialogState.customer?.id}`}
                  ref={materialRatesDataGridRef}
                  companyId={companyId}
                  customerId={materialRateDialogState.customer?.id}
                  materialIds={appliedFilters.materials}
                  onDelete={handleDeleteMaterialRate}
                  onEdit={handleEditMaterialRate}
                  projectIds={appliedFilters.projects}
                  siteIds={appliedFilters.sites}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Dialog
        sx={{ [`& .${paperClasses.root}`]: { maxWidth: '500px', overflowY: 'visible' } }}
        isOpen={materialRateDialogState.isOpen}
        onClose={() => {
          if (!materialRateDialogState.isSaving) {
            closeDialog();
          }
        }}
        preventCloseOnBackdropClick
      >
        <DialogHeader
          onClose={() => {
            if (!materialRateDialogState.isSaving) {
              closeDialog();
            }
          }}
        >
          {dialogTitle}
        </DialogHeader>

        <DialogContent>
          <MaterialRateForm
            ref={materialRateFormRef}
            companyId={companyId}
            customer={
              materialRateDialogState.source === MaterialRateDialogSource.ADD_SCHEDULE_CTA
                ? undefined
                : materialRateDialogState.customer
            }
            errors={materialRateDialogState.errors}
            materialRate={materialRateDialogState.materialRate}
          />
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={materialRateDialogState.isSaving}
            onClick={closeDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="brandV2Yellow"
            disabled={materialRateDialogState.isSaving}
            loading={materialRateDialogState.isSaving}
            onClick={handleMaterialRateFormSubmit}
          >
            {isEditingMaterialRate ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default MaterialRates;
